import {
  Autocomplete,
  Box,
  CircularProgress,
  ListItem,
  Popper,
  Skeleton,
  TextField,
  Typography
} from '@mui/material';
import { BusinessProfileAutocompleteStyles } from './BusinessProfileAutocomplete.styles';
import { useBusinessProfileAutocomplete, BusinessProfileOption } from './BusinessProfileAutocompleteContext';

interface BusinessProfileAutocompleteProps {
  placeholder?: string;
  noOptionsText?: string | ((inputValue: string) => string);
  isOptionEqualToValue?: (
    option: BusinessProfileOption,
    value: BusinessProfileOption
  ) => boolean;
  getOptionLabel?: (option: BusinessProfileOption) => string;
  sx?: any;
}

export const BusinessProfileAutocomplete = ({
  placeholder = 'Enter name',
  noOptionsText,
  isOptionEqualToValue,
  getOptionLabel,
  sx
}: BusinessProfileAutocompleteProps) => {
  const {
    inputValue,
    selectedOption,
    loading,
    displayedOptions,
    handleInputChange,
    handleChange
  } = useBusinessProfileAutocomplete();

  const defaultIsOptionEqualToValue = (
    option: BusinessProfileOption,
    value: BusinessProfileOption
  ) => option?.placeId === value?.placeId && !option.isSkeleton;

  const defaultGetOptionLabel = (option: BusinessProfileOption) => {
    // For skeleton options, use placeId to ensure uniqueness
    if (option.isSkeleton) {
      return option.placeId || `skeleton-${Math.random()}`;
    }
    // For real options, use name or fallback to placeId
    return option.name || option.placeId || '';
  };

  const defaultNoOptionsText = inputValue
    ? 'No results found'
    : 'Start typing...';

  return (
    <Autocomplete
      freeSolo
      options={displayedOptions}
      isOptionEqualToValue={
        isOptionEqualToValue || defaultIsOptionEqualToValue
      }
      getOptionLabel={getOptionLabel || defaultGetOptionLabel}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      value={selectedOption}
      onChange={handleChange}
      disableClearable
      popupIcon={null}
      loading={loading}
      loadingText="Searching…"
      noOptionsText={
        typeof noOptionsText === 'function'
          ? noOptionsText(inputValue)
          : noOptionsText || defaultNoOptionsText
      }
      filterOptions={(x) => x}
      sx={{
        ...BusinessProfileAutocompleteStyles.autoCompleteSx,
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: inputValue ? 'var(--color-primary)' : 'var(--color-border-light2)'
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: inputValue ? 'var(--color-primary)' : 'var(--color-text-light-grey)'
        },
        '& .MuiOutlinedInput-root': {
          color: inputValue ? 'var(--color-primary)' : 'rgba(0, 0, 0, 0.40)',
          fontWeight: 400
        },
        ...sx
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            )
          }}
          sx={BusinessProfileAutocompleteStyles.textFieldSx}
        />
      )}
      renderOption={(props, option) => {
        const { key, ...listItemProps } = props as any;
        // Use placeId as key to ensure uniqueness
        const uniqueKey = option?.placeId || key || `option-${Math.random()}`;
        
        // If loading, show skeletons — this will run for fake placeholder options
        if (loading && option?.isSkeleton) {
          return (
            <ListItem key={uniqueKey} {...listItemProps} sx={BusinessProfileAutocompleteStyles.listItem}>
              <Box width="100%">
                <Skeleton width="80%" height={20} sx={{ mb: 1 }} />
                <Skeleton width="60%" height={16} />
              </Box>
            </ListItem>
          );
        }

        // Normal option rendering
        return (
          <ListItem key={uniqueKey} {...listItemProps} sx={BusinessProfileAutocompleteStyles.listItem}>
            <Typography sx={BusinessProfileAutocompleteStyles.name}>
              {option?.name}
            </Typography>
            <Typography sx={BusinessProfileAutocompleteStyles.address}>
              {option?.address}
            </Typography>
          </ListItem>
        );
      }}
      PopperComponent={(props: any) => {
        const anchorEl = props.anchorEl as HTMLElement;
        return (
          <Popper
            {...props}
            placement="bottom-start"
            style={{
              width: anchorEl?.clientWidth || undefined,
              zIndex: 1300
            }}
          />
        );
      }}
    />
  );
};
